// import "./style.css";
import { html, LitElement } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { customElement } from "lit/decorators.js";
import "@ingka/modal-webc";
import "@ingka/input-field-webc";
import "@ingka/switch-webc";
import hotkeys from "hotkeys-js";
import { marked } from "marked";

@customElement("ffp-chatbox")
export class ChatBoxElement extends LitElement {
  open: boolean = true;
  placeholder: string = "Please type your message here";
  messages: Array<{ role: string; content: string }> = [];
  prompt: string = "";
  isThinking: boolean = false;
  thinkingMessage: string = "Thinking...";

  // Inherit the global css for tailwind by creating a lite dom without shadowRoot
  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }
  static events = {
    PROMPT_SUBMIT: "PROMPT_SUBMIT",
  };
  static get properties() {
    return {
      open: { type: Boolean },
      messages: {
        type: Array,
      },
      prompt: { type: String },
      placeholder: { type: String },
      isThinking: { type: Boolean },
      thinkingMessage: { type: String },
    };
  }
  updated(changedProperties: any) {
    if (changedProperties.has("messages")) {
      this.scrollToBottom();
    }
  }
  scrollToBottom() {
    // Scroll to bottom runs asynchronously
    // because when a value changes component will re-render
    // loosing the scroll position
    setTimeout(() => {
      const chatContainer = this.querySelector(
        ".scrollable-content"
      ) as HTMLElement;
      chatContainer.scrollTo({
        top: chatContainer.scrollHeight,
        behavior: "smooth",
      });
    }, 0);
  }
  firstUpdated() {
    hotkeys("command+k, ctrl+k", () => {
      this.open = !this.open;
      setTimeout(() => {
        (this.querySelector("#prompt-input") as HTMLElement).focus();
      }, 100);
    });

    // Update self attibute if the child skapa-sheet is closed
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "open"
        ) {
          this.open = (mutation.target as any).open;
        }
      });
    });
    observer.observe(this.querySelector("skapa-sheet"), {
      attributes: true,
    });
  }
  sendMessage(event: SubmitEvent) {
    event.preventDefault();

    const input = this.querySelector("#prompt-input") as HTMLInputElement;
    const value = input.value;
    if (value.trim() === "") {
      return;
    }
    input.value = "";

    this.dispatchEvent(
      new CustomEvent(ChatBoxElement.events.PROMPT_SUBMIT, {
        detail: {
          message: value,
        },
      })
    );
  }
  /**
   * Adds a message in the chat
   * @param {string} content
   * @param {string} role
   */
  addMessage(content: string, role: string) {
    this.messages = [
      ...this.messages,
      {
        role,
        content,
      },
    ];
  }
  /**
   * Returns a function that can be used to stop the thinking process
   * @param {string} message
   * @returns {Function}
   */
  think(message: string) {
    this.thinkingMessage = message;
    this.isThinking = true;
    return () => {
      this.thinkingMessage = "";
      this.isThinking = false;
    };
  }
  render() {
    // Is it a good idea to use zod or some other schema validation library to validate the props here?

    return html`
      <skapa-sheet
        .open=${this.open}
        aria-label="Accessible label for dialog"
        size="small"
      >
        <!-- Scroll content -->
        <div class="flex flex-col h-full pt-2">
          <div
            class="flex-1 flex flex-col overflow-y-scroll scrollable-content"
          >
            ${this.messages.length === 0
              ? html`
                  <div class="no-message-notice">
                    <p>
                      Start a conversation by typing in the input field below.
                    </p>
                    <p>
                      Press <kbd>Ctrl/Cmd</kbd> + <kbd>K</kbd> to toggle the
                      chat
                      <br />
                      Press <kbd>Ctrl/Cmd</kbd> + <kbd>J</kbd> to toggle the
                      chat mode
                      <br />
                      Press <kbd>Enter</kbd> to send a message
                    </p>
                  </div>
                `
              : ""}
            ${this.messages.map(
              /** @param {import("../InferenceApi.js").ChatMessage} message */
              (message) => html`
                <div
                  class="message-wrapper ${["user", "assistant"].includes(
                    message.role
                  )
                    ? message.role
                    : ""}"
                >
                  <div
                    class="message [&_td]:p-1 [&_tr]:border-b [&_tr]:border-gray-200"
                  >
                    ${
                      /** @ts-ignore */
                      unsafeHTML(marked.parse(message.content))
                    }
                  </div>
                </div>
              `
            )}
            ${this.isThinking
              ? html`
                  <div id="loading-container">
                    <div id="loading-ball">
                      <skapa-loading-ball></skapa-loading-ball>
                    </div>
                    <div style="text-align:center">
                      <span slot="message">${this.thinkingMessage}</span>
                    </div>
                  </div>
                `
              : ``}
          </div>
          <div class="scrollable-footer">
            <form @submit=${this.sendMessage} class="flex flex-row">
              <skapa-input-field
                auto-id="prompt-input"
                state="regular"
                style="max-width: auto"
                class="flex-1"
                @input=${(this as any).setPrompt}
              >
                <label slot="label"><!-- no label --></label>
                <input
                  id="prompt-input"
                  .placeholder=${this.placeholder}
                  .value=${this.prompt}
                  type="text"
                />
                ${this.messages.length === 0
                  ? html`<skapa-helper-text slot="helper"
                      >Please type a message to get started.</skapa-helper-text
                    >`
                  : ""}
              </skapa-input-field>
              <skapa-button
                type="submit"
                variant="emphasised"
                size="small"
                class="m-1"
              >
                Send
              </skapa-button>
            </form>
          </div>
        </div>
        <!-- Scroll content END -->
      </skapa-sheet>
    `;
  }
}

if (!customElements.get("ffp-chatbox")) {
  customElements.define("ffp-chatbox", ChatBoxElement);
}